/**
 * Root-level error boundary.
 *
 * Catches anything that escapes React render (e.g. a hook throws, a child
 * component crashes during state update) and shows a friendly fallback
 * with a "reload" button instead of a blank white screen. When Sentry is
 * on, the error is also reported automatically, the way Sentry's own
 * ErrorBoundary reports one (lib/sentry.ts captureSentryReactCrash). The
 * boundary is the Hub's own class below rather than Sentry.ErrorBoundary,
 * because that component put the whole Sentry SDK into the startup bundle
 * (speed plan 2026-10-19, P2); the SDK now loads after the page is up.
 *
 * The fallback is intentionally self-contained - no Firebase, no auth, no
 * router. If the app's basic plumbing is what crashed, those dependencies
 * may also be broken, and the goal of the fallback is to show *something*
 * rather than a stack trace in the console.
 */

import { Component, useState, type ErrorInfo, type ReactNode } from 'react';
import { Icon } from './Icon';
import { useAppOnScreenHandshake } from '../lib/bootHandshake';
import { captureSentryReactCrash, reportErrorBoundaryIssue } from '../lib/sentry';
import { crashingComponentLabel, firstFramesOfComponentStack } from '../lib/componentStack';


interface FallbackProps {
  error: unknown;
  resetError: () => void;
  /**
   * React's own component stack for the crash, handed to us by
   * Sentry.ErrorBoundary. THIS IS THE WHOLE POINT OF THE PANEL BELOW.
   *
   * A production build reports a crash as "Minified React error #310" and
   * nothing else, which names no file, no component and no route. On
   * 2026-08-26 Bobbie hit exactly that twice, on a phone and on a desktop,
   * and it could not be placed: a static hunt across src found no
   * conditionally called hook and no hook after an early return anywhere,
   * so there was nothing to fix without knowing WHERE it fired. The stack
   * was available the whole time and this component was dropping it on the
   * floor.
   *
   * TYPED OPTIONAL FOR A REASON THAT IS NOT THE OBVIOUS ONE. An earlier comment
   * here claimed it is "absent when Sentry is not initialized", and review
   * proved that false: @sentry/react takes componentStack from React's own
   * errorInfo in componentDidCatch, which has nothing to do with Sentry.init,
   * and its fallback only renders once that value is non-null. The real reason
   * to guard is that Sentry's FallbackRender type declares `componentStack:
   * string` while its own state types it as React's
   * ErrorInfo['componentStack'], which @types/react declares `string | null`.
   * The published type is more confident than the runtime, so every use of it
   * is guarded.
   */
  componentStack?: string;
}

/**
 * A lazy chunk that failed to IMPORT is not a broken Hub, it is (almost
 * always) a tab that outlived a deploy: the new version replaced the hashed
 * files this shell was built against, lazyRoute's one automatic reload for
 * that version was already spent (or the retry itself failed on a weak
 * connection), and the import landed here. Kim hit exactly this on
 * 2026-08-29 - "It broke in Lazy / Importing a module script failed" - and
 * the screen told her to submit an error report for what was really "the
 * app updated, reload". The three signatures are the three engines' own
 * wordings (Safari, Chrome, Firefox) plus webpack-era ChunkLoadError for
 * completeness; nothing else matches them.
 */
function isStaleImportError(message: string): boolean {
  return /Importing a module script failed|dynamically imported module|ChunkLoadError/i
    .test(message);
}

function Fallback({ error, resetError, componentStack }: FallbackProps) {
  // This screen IS something on screen, and it is the one people are asked
  // to screenshot and send. Disarm index.html's 15 second reload offer from
  // its commit, or the watchdog would replace the error report screen with a
  // plain "The Hub didn't load" while somebody was reading it. A crash so
  // complete that even this cannot render never reaches here, which is
  // exactly the case the offer is for. See lib/bootHandshake.ts.
  useAppOnScreenHandshake();
  const [reportState, setReportState] = useState<'idle' | 'sent' | 'unavailable'>('idle');
  const message = error instanceof Error ? error.message : String(error);
  const staleImport = isStaleImportError(message);
  const stackHead = firstFramesOfComponentStack(componentStack);
  const brokeIn = crashingComponentLabel(componentStack);
  const submitReport = () => {
    const sent = reportErrorBoundaryIssue(error, componentStack);
    setReportState(sent ? 'sent' : 'unavailable');
  };

  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        textAlign: 'center',
        gap: 16,
        background: '#fff',
        color: '#1a1a1a',
        fontFamily:
          'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div style={{ color: 'var(--amber-text)' }}><Icon name="warning" size={40} /></div>
      <h1 style={{ margin: 0, fontSize: 22 }}>
        {staleImport ? 'A reload should fix this' : 'Something broke'}
      </h1>
      <p style={{ margin: 0, maxWidth: 390, color: '#555', fontSize: 14 }}>
        {staleImport
          // NEVER "the Hub just updated" as a flat claim: an offline device
          // and a genuinely broken chunk throw the SAME import-error strings
          // (Codex review of #779), so the copy names the usual cause,
          // covers the other one, and promises only what is true in all
          // three cases - the failure never touches their account or work.
          ? 'A piece of this page could not load. That usually means a new version of '
            + 'the Hub arrived while this tab was open, and a reload picks it up. If '
            + 'reloading does not fix it, check your connection - a weak signal looks '
            + 'exactly the same. Your account and your work are not affected either way.'
          : 'The Hub hit an error and could not keep going. Submit the error report here, '
            + 'then reload and try again.'}
      </p>
      {/* THIS LINE IS THE POINT OF THE WHOLE CHANGE, AND IT IS DELIBERATELY NOT
          INSIDE THE <details> BELOW. A details element with no `open` attribute
          is collapsed, so putting the component name there left a screenshot of
          this screen saying exactly what it said before: nothing about WHERE.
          Adversarial review caught that after the first version shipped it that
          way. Bobbie diagnoses crashes by sending a screenshot, so the name has
          to survive the screenshot. */}
      {brokeIn && !staleImport && (
        <p style={{ margin: 0, maxWidth: 390, color: '#555', fontSize: 14 }}>
          It broke in{' '}
          <code
            style={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 13,
              fontWeight: 700,
              color: '#1a1a1a',
              background: '#f0f0f0',
              padding: '2px 6px',
              borderRadius: 6,
              wordBreak: 'break-word',
            }}
          >
            {brokeIn}
          </code>
        </p>
      )}
      {/* Reload leads when the crash IS the update: asking someone to file a
          report about a deploy landing would collect noise and bury the one
          button that fixes it. The report stays available underneath for the
          rare stale-import that is really a broken network. */}
      <div
        style={{
          display: 'flex',
          flexDirection: staleImport ? 'column-reverse' : 'column',
          gap: 10,
          width: '100%',
          maxWidth: 340,
        }}
      >
        <button
          type="button"
          onClick={submitReport}
          disabled={reportState === 'sent'}
          style={{
            background: '#fff',
            color: '#1a1a1a',
            fontWeight: 800,
            fontSize: 16,
            padding: '12px 24px',
            minHeight: 44,
            borderRadius: 10,
            border: '1.5px solid #d7d7d7',
            cursor: reportState !== 'sent' ? 'pointer' : 'not-allowed',
          }}
        >
          {reportState === 'sent' ? 'Error report submitted' : 'Submit error report'}
        </button>
        {reportState === 'unavailable' && (
          <p style={{ margin: 0, color: '#777', fontSize: 12 }}>
            Error reporting is not turned on for this build yet. Reload and try again.
          </p>
        )}
        {reportState === 'sent' && (
          <p style={{ margin: 0, color: '#3d6b13', fontSize: 12 }}>
            Thanks. The report includes technical details from this screen.
          </p>
        )}
        <button
          type="button"
          onClick={() => {
            resetError();
            // Hard reload in case the error left React state in a bad spot.
            window.location.reload();
          }}
          style={{
            background: '#A8E63A',
            color: '#0E1A0A',
            fontWeight: 800,
            fontSize: 16,
            padding: '12px 24px',
            minHeight: 44,
            borderRadius: 10,
            border: 0,
            cursor: 'pointer',
          }}
        >
          Reload the Hub
        </button>
      </div>
      <details
        style={{
          marginTop: 16,
          fontSize: 12,
          color: '#888',
          maxWidth: 360,
          textAlign: 'left',
        }}
      >
        <summary
          style={{
            cursor: 'pointer',
            minHeight: 44,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          Technical detail
        </summary>
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            background: '#f5f5f5',
            padding: 12,
            borderRadius: 8,
            marginTop: 8,
            fontSize: 11,
          }}
        >
          {stackHead ? `${message}\n\nWhere it happened:\n${stackHead}` : message}
        </pre>
      </details>
    </div>
  );
}

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface FallbackRenderProps {
  error: unknown;
  resetError: () => void;
  componentStack: string | null | undefined;
}

interface HubErrorBoundaryProps {
  fallback: (props: FallbackRenderProps) => ReactNode;
  children: ReactNode;
}

interface HubErrorBoundaryState {
  error: unknown;
  failed: boolean;
  componentStack: string | null | undefined;
}

/**
 * The same contract Sentry.ErrorBoundary gave this file: catch, report with
 * React's component stack, render the fallback with error, resetError and
 * componentStack, and reset back to the children on resetError.
 */
class HubErrorBoundary extends Component<HubErrorBoundaryProps, HubErrorBoundaryState> {
  state: HubErrorBoundaryState = { error: null, failed: false, componentStack: undefined };

  static getDerivedStateFromError(error: unknown): Partial<HubErrorBoundaryState> {
    return { error, failed: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    const componentStack = info.componentStack ?? undefined;
    captureSentryReactCrash(error, componentStack);
    this.setState({ componentStack });
  }

  resetError = (): void => {
    this.setState({ error: null, failed: false, componentStack: undefined });
  };

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return this.props.fallback({
      error: this.state.error,
      resetError: this.resetError,
      componentStack: this.state.componentStack,
    });
  }
}

function AppErrorBoundary({ children }: AppErrorBoundaryProps) {
  return (
    <HubErrorBoundary
      fallback={({ error, resetError, componentStack }) => (
        <Fallback
          error={error}
          resetError={resetError}
          componentStack={typeof componentStack === 'string' ? componentStack : undefined}
        />
      )}
    >
      {children}
    </HubErrorBoundary>
  );
}

export default AppErrorBoundary;
